import type { ReactNode } from 'react';
import { authApi, agencyApi, setTokens, clearTokens } from '../lib/api';
import type { UserInfo, AgencyProfile } from '../lib/api';

interface AuthContextType {
  user: UserInfo | null;
  profile: AgencyProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (accessToken: string, refreshToken: string, user: UserInfo) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [profile, setProfile] = useState<AgencyProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const stored = sessionStorage.getItem('user');
    const token = sessionStorage.getItem('access_token');
    if (stored && token) {
      try {
        setUser(JSON.parse(stored));
        agencyApi.getMe().then(res => setProfile(res.data)).catch(() => {});
      } catch { clearTokens(); }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const handle = () => { setUser(null); setProfile(null); };
    window.addEventListener('auth:unauthorized', handle);
    return () => window.removeEventListener('auth:unauthorized', handle);
  }, []);

  const login = (accessToken: string, refreshToken: string, user: UserInfo) => {
    setTokens(accessToken, refreshToken);
    sessionStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    agencyApi.getMe().then(res => setProfile(res.data)).catch(() => {});
  };

  const logout = async () => {
    try { await authApi.logout(); } catch {}
    clearTokens();
    setUser(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider value={{ user, profile, isAuthenticated: !!user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
